/**
 * Email Marketing Module - Unified Email Platform Management
 * Supports: GetResponse, EmailOctopus, and other email marketing platforms
 * Full CRUD operations for contacts, lists, campaigns
 * E-shop sync and automation for abandoned carts/consultations
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Send,
  Users,
  List,
  Mail,
  RefreshCw,
  Plus,
  Upload,
  Download,
  Settings,
  Zap,
  ShoppingCart,
  MessageSquare,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Search,
  Filter,
  X,
  LayoutGrid,
  LayoutList,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useDataSourceStore } from '@store/dataSourceStore';
import { useAuthStore } from '@store/authStore';
import { api } from '@services/api';
import { toast } from 'react-hot-toast';
import ListSettingsModal from '@components/email/ListSettingsModal';
import { ContactCreateModal } from '@components/email/ContactCreateModal';
import { ContactEditModal } from '@components/email/ContactEditModal';
import { ContactDetailModal } from '@components/email/ContactDetailModal';
import { ImportFromEshopModal } from '@components/email/ImportFromEshopModal';
import ListDetailModal from '@components/email/ListDetailModal';

interface Contact {
  contactId: string;
  email: string;
  name?: string;
  campaign: {
    campaignId: string;
    name: string;
  };
  createdOn: string;
  tags?: Array<{ tagId: string; name: string }>;
}

interface CampaignList {
  campaignId: string;
  name: string;
  description?: string;
  languageCode: string;
  isDefault: boolean;
  createdAt: string;
}

interface Newsletter {
  newsletterId: string;
  name: string;
  subject: string;
  status: string;
  sendOn?: string;
  createdOn: string;
  campaign: {
    campaignId: string;
    name: string;
  };
}

const GetResponsePage = () => {
  const { selectedCompany, selectedDataSources, getSelection } = useDataSourceStore();
  const { companyId, dataSourceIds } = getSelection();
  const { accessToken } = useAuthStore();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<'contacts' | 'lists' | 'campaigns' | 'sync'>(
    'contacts'
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedList, setSelectedList] = useState<string>('');
  const [selectedPlatform, setSelectedPlatform] = useState<string>('');
  const [listsSearchQuery, setListsSearchQuery] = useState('');

  // View mode state (card or list)
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');
  const [listsViewMode, setListsViewMode] = useState<'card' | 'list'>('list');

  // Sorting state for contacts datatable
  const [sortBy, setSortBy] = useState<'email' | 'name' | 'createdOn'>('createdOn');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Sorting state for lists datatable
  const [listsSortBy, setListsSortBy] = useState<'name' | 'contacts' | 'createdAt'>('name');
  const [listsSortOrder, setListsSortOrder] = useState<'asc' | 'desc'>('asc');

  // Pagination state for contacts
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsLimit] = useState(50); // ✅ Increased from 20 to 50 contacts per page

  // Create List Modal State
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');

  // Edit List Modal State - SEPARATE STATE to avoid conflicts
  const [showEditListModal, setShowEditListModal] = useState(false);
  const [editingList, setEditingList] = useState<CampaignList | null>(null);
  const [editListName, setEditListName] = useState('');
  const [editListDescription, setEditListDescription] = useState('');
  const [editListIsDefault, setEditListIsDefault] = useState(false);

  // Full Settings Modal State
  const [showListSettings, setShowListSettings] = useState(false);
  const [settingsList, setSettingsList] = useState<CampaignList | null>(null);

  // View List Modal State
  const [showViewListModal, setShowViewListModal] = useState(false);
  const [viewingList, setViewingList] = useState<CampaignList | null>(null);

  // Contact Modals State
  const [showCreateContactModal, setShowCreateContactModal] = useState(false);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [showDetailContactModal, setShowDetailContactModal] = useState(false);
  const [detailModalStartInEditMode, setDetailModalStartInEditMode] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [viewingContact, setViewingContact] = useState<Contact | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Bulk Actions State
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Get ALL email marketing data sources (GetResponse, EmailOctopus, etc.)
  const emailMarketingPlatforms =
    selectedDataSources?.filter(
      ds =>
        ds.type === 'getresponse' ||
        ds.type === 'emailoctopus' ||
        ds.config?.getresponseApiKey ||
        ds.config?.emailoctopusApiKey
    ) || [];

  // Auto-select first platform if none selected
  useEffect(() => {
    if (!selectedPlatform && emailMarketingPlatforms.length > 0) {
      setSelectedPlatform(emailMarketingPlatforms[0].id);
    }
  }, [emailMarketingPlatforms, selectedPlatform]);

  // Get currently selected email platform
  const emailMarketingDataSource = emailMarketingPlatforms.find(ds => ds.id === selectedPlatform);

  // For backward compatibility
  const getResponseDataSource = emailMarketingDataSource;

  // Determine platform name for display
  const platformName =
    emailMarketingDataSource?.type === 'getresponse'
      ? 'GetResponse'
      : emailMarketingDataSource?.type === 'emailoctopus'
      ? 'EmailOctopus'
      : emailMarketingDataSource?.config?.getresponseApiKey
      ? 'GetResponse'
      : emailMarketingDataSource?.config?.emailoctopusApiKey
      ? 'EmailOctopus'
      : 'Email Marketing Platform';

  const dataSourceId = getResponseDataSource?.id;

  // Manual refresh state
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);

  // Fetch Contacts with server-side pagination & sorting
  const {
    data: contactsData,
    isLoading: contactsLoading,
    refetch: refetchContacts,
  } = useQuery({
    queryKey: [
      'getresponse-contacts',
      companyId,
      dataSourceId,
      selectedList,
      searchQuery,
      sortBy,
      sortOrder,
      contactsPage,
      contactsLimit,
    ],
    queryFn: async () => {
      if (!companyId || !dataSourceId) return { contacts: [], total: 0, page: 1, totalPages: 1 };

      const params: any = {
        companyId,
        dataSourceId,
        sortBy,
        sortOrder,
        page: contactsPage,
        limit: contactsLimit,
      };

      if (selectedList) params.campaignId = selectedList;
      if (searchQuery) params.email = searchQuery;

      console.log('📊 [GetResponse] Fetching contacts with params:', params);
      console.log('📊 [GetResponse] selectedList:', selectedList, 'searchQuery:', searchQuery);

      const response = await api.get('/api/v1/getresponse/contacts', { params });
      console.log('📊 [GetResponse] Response:', response.data.data);
      return response.data.data;
    },
    enabled: !!companyId && !!dataSourceId && activeTab === 'contacts',
    staleTime: 0, // Always consider data stale
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  // Fetch all contacts for counting in Lists tab
  // Get contact count from list (now calculated and stored in backend!)
  const getContactCountForList = (campaignId: string) => {
    const list = listsData?.lists?.find((l: CampaignList) => l.campaignId === campaignId);
    return list?.contactsCount || 0;
  };

  // Fetch Lists
  const {
    data: listsData,
    isLoading: listsLoading,
    refetch: refetchLists,
  } = useQuery({
    queryKey: ['getresponse-lists', companyId, dataSourceId],
    queryFn: async () => {
      if (!companyId || !dataSourceId) return { lists: [], total: 0 };

      const response = await api.get('/api/v1/getresponse/lists', {
        params: { companyId, dataSourceId, perPage: 100 },
      });
      return response.data.data;
    },
    enabled: !!companyId && !!dataSourceId,
    staleTime: 0, // Always consider data stale
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  // Fetch Campaigns (Newsletters)
  const {
    data: campaignsData,
    isLoading: campaignsLoading,
    refetch: refetchCampaigns,
  } = useQuery({
    queryKey: ['getresponse-campaigns', companyId, dataSourceId],
    queryFn: async () => {
      if (!companyId || !dataSourceId) return { campaigns: [], overall: {} };

      const response = await api.get('/api/v1/getresponse/campaigns', {
        params: { companyId, dataSourceId, perPage: 100 },
      });
      return response.data.data;
    },
    enabled: !!companyId && !!dataSourceId && activeTab === 'campaigns',
    staleTime: 0, // Always consider data stale
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  // Force manual sync - Calls backend to fetch fresh data from GetResponse API
  const handleManualSync = useCallback(
    async (silent = false) => {
      if (!silent) {
        console.log('🔄 [GetResponse] Manual sync: Fetching fresh data from GetResponse API...');
      }
      setSyncing(true);
      try {
        // Call backend sync endpoint - this fetches from GetResponse API & clears cache
        const response = await api.post('/api/v1/getresponse/sync', {
          companyId,
          dataSourceId,
        });

        console.log('✅ [GetResponse] API sync completed:', response.data.data);

        // Wait 500ms to ensure cache is fully cleared
        await new Promise(resolve => setTimeout(resolve, 500));

        // Now refetch to get the fresh data
        await Promise.all([refetchContacts(), refetchLists(), refetchCampaigns()]);

        setLastSyncTime(new Date());
        if (!silent) {
          const { contactsCount, listsCount, campaignsCount } = response.data.data;
          console.log(
            `✅ [GetResponse] Synced: ${contactsCount} contacts, ${listsCount} lists, ${campaignsCount} campaigns`
          );
          toast.success(`✅ Synced! ${contactsCount} contacts, ${listsCount} lists`);
        }
      } catch (error: any) {
        console.error('❌ [GetResponse] Sync error:', error);
        if (!silent) {
          toast.error(error.response?.data?.message || '❌ Sync failed');
        }
      } finally {
        setSyncing(false);
      }
    },
    [companyId, dataSourceId, refetchContacts, refetchLists, refetchCampaigns]
  );

  // WebSocket Real-Time Updates - TRUE LIVE SYNC!
  useEffect(() => {
    if (!companyId || !dataSourceId) return;

    console.log('🔌 [GetResponse] Connecting to WebSocket for REAL-TIME updates...');

    // Initial sync
    handleManualSync(true);

    // Import WebSocket from your existing WebSocket hook
    import('@hooks/useWebSocket').then(({ socket }) => {
      if (!socket) {
        console.warn('⚠️  WebSocket not available, falling back to polling');
        setIsWebSocketConnected(false);
        return;
      }

      // Mark WebSocket as connected
      setIsWebSocketConnected(true);
      console.log('✅ [WebSocket] Connected for real-time updates');

      // Listen for GetResponse events with toast notifications
      const events = [
        'getresponse:contact:created',
        'getresponse:contact:updated',
        'getresponse:contact:deleted',
        'getresponse:list:created',
        'getresponse:list:updated',
        'getresponse:list:deleted',
        'getresponse:campaign:created',
        'getresponse:campaign:sent',
        'getresponse:update', // Generic update event
      ];

      events.forEach(event => {
        socket.on(event, (data: any) => {
          console.log(`⚡ [WebSocket] ${event}:`, data);

          // Update UI immediately based on event type with user notifications
          if (event === 'getresponse:contact:created') {
            toast.success('🆕 New contact added from GetResponse!');
            refetchContacts();
          } else if (event === 'getresponse:contact:updated') {
            toast.info('📝 Contact updated in GetResponse');
            refetchContacts();
          } else if (event === 'getresponse:contact:deleted') {
            toast.info('🗑️ Contact deleted in GetResponse');
            refetchContacts();
          } else if (event === 'getresponse:list:created') {
            toast.success('🆕 New list created in GetResponse!');
            refetchLists();
          } else if (event === 'getresponse:list:updated') {
            toast.info('📝 List updated in GetResponse');
            refetchLists();
          } else if (event === 'getresponse:list:deleted') {
            toast.info('🗑️ List deleted in GetResponse');
            refetchLists();
          } else if (event.includes('campaign')) {
            toast.info('📧 Campaign updated in GetResponse');
            refetchCampaigns();
          } else if (event === 'getresponse:update') {
            // Generic update - refresh everything
            toast.info('🔄 GetResponse data updated');
            handleManualSync(true);
          }

          setLastSyncTime(new Date());
        });
      });

      console.log('✅ [GetResponse] WebSocket listeners registered for REAL-TIME updates');

      // Cleanup
      return () => {
        events.forEach(event => socket.off(event));
        console.log('🔌 [GetResponse] WebSocket listeners removed');
      };
    });

    // Fallback polling every 30 seconds (in case WebSocket fails)
    const fallbackInterval = setInterval(() => {
      console.log('🔄 [GetResponse] Fallback sync (30s)');
      handleManualSync(true);
    }, 30000);

    return () => {
      clearInterval(fallbackInterval);
    };
  }, [companyId, dataSourceId, handleManualSync, refetchContacts, refetchLists, refetchCampaigns]);

  // Import from E-shop
  const importFromEshopMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/v1/getresponse/import/eshop', {
        companyId,
        dataSourceId,
      });
      return response.data;
    },
    onSuccess: data => {
      toast.success(`Imported ${data.data.created} contacts successfully!`);
      refetchContacts();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to import contacts');
    },
  });

  // Delete Contact
  const deleteContactMutation = useMutation({
    mutationFn: async (contactId: string) => {
      console.log('🗑️ Deleting contact:', contactId);
      const response = await api.delete(`/api/v1/getresponse/contacts/${contactId}`, {
        params: { companyId, dataSourceId },
      });
      return response.data;
    },
    onSuccess: async (data, contactId) => {
      console.log('✅ Contact deleted:', contactId);
      toast.success('Contact deleted successfully!');

      // Force immediate refetch with error handling
      try {
        await refetchContacts();
        console.log('✅ Contacts refetched after delete');
      } catch (error) {
        console.error('❌ Error refetching contacts:', error);
        // Still show success since delete worked
      }
    },
    onError: (error: any) => {
      console.error('❌ Delete contact error:', error);
      toast.error(error.response?.data?.message || 'Failed to delete contact');
    },
  });

  // Bulk Delete Contacts
  const bulkDeleteMutation = useMutation({
    mutationFn: async (contactIds: string[]) => {
      const response = await api.post('/api/v1/getresponse/contacts/bulk-delete', {
        companyId,
        dataSourceId,
        contactIds,
      });
      return response.data;
    },
    onSuccess: async data => {
      const { deleted, failed } = data.data;
      toast.success(`Deleted ${deleted} contact(s)${failed > 0 ? `, ${failed} failed` : ''}`);
      setSelectedContacts(new Set());
      setShowBulkActions(false);
      // Force immediate refetch
      await refetchContacts();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete contacts');
    },
  });

  // Bulk Move Contacts
  const bulkMoveMutation = useMutation({
    mutationFn: async ({
      contactIds,
      targetCampaignId,
    }: {
      contactIds: string[];
      targetCampaignId: string;
    }) => {
      const response = await api.post('/api/v1/getresponse/contacts/bulk-move', {
        companyId,
        dataSourceId,
        contactIds,
        targetCampaignId,
      });
      return response.data;
    },
    onSuccess: async data => {
      const { moved, failed } = data.data;
      toast.success(`Moved ${moved} contact(s)${failed > 0 ? `, ${failed} failed` : ''}`);
      setSelectedContacts(new Set());
      setShowBulkActions(false);
      // Force immediate refetch
      await refetchContacts();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to move contacts');
    },
  });

  // Create List
  const createListMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/v1/getresponse/lists', {
        companyId,
        dataSourceId,
        name: newListName,
        description: newListDescription,
        languageCode: 'EN',
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('List created successfully!');
      setShowCreateListModal(false);
      setNewListName('');
      setNewListDescription('');
      refetchLists();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to create list');
    },
  });

  // Delete List
  const deleteListMutation = useMutation({
    mutationFn: async (listId: string) => {
      const response = await api.delete(`/api/v1/getresponse/lists/${listId}`, {
        params: { companyId, dataSourceId },
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('List deleted successfully!');
      refetchLists();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to delete list');
    },
  });

  // Update List
  const updateListMutation = useMutation({
    mutationFn: async ({
      listId,
      name,
      description,
      isDefault,
    }: {
      listId: string;
      name: string;
      description: string;
      isDefault?: boolean;
    }) => {
      const response = await api.put(`/api/v1/getresponse/lists/${listId}`, {
        companyId,
        dataSourceId,
        name,
        description,
        isDefault,
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('List updated successfully!');
      setShowEditListModal(false);
      setEditingList(null);
      setEditListName('');
      setEditListDescription('');
      setEditListIsDefault(false);
      refetchLists();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to update list');
    },
  });

  // Note: List contacts are now fetched inside ListDetailModal

  // Sync Abandoned Carts
  const syncAbandonedCartsMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/v1/getresponse/sync/abandoned-carts', {
        companyId,
        dataSourceId,
      });
      return response.data;
    },
    onSuccess: data => {
      toast.success(`Synced ${data.data.synced} abandoned carts!`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to sync abandoned carts');
    },
  });

  const handleRefresh = () => {
    // Use the comprehensive manual sync instead
    handleManualSync();
  };

  // Handle sorting for contacts table
  const handleContactsSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setContactsPage(1); // Reset to first page when sorting changes
  };

  // Handle pagination
  const goToContactsPage = (page: number) => {
    const totalPages = contactsData?.totalPages || 1;
    setContactsPage(Math.max(1, Math.min(page, totalPages)));
  };

  // Handle sorting for lists table
  const handleListsSort = (column: typeof listsSortBy) => {
    if (listsSortBy === column) {
      setListsSortOrder(listsSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setListsSortBy(column);
      setListsSortOrder('asc');
    }
  };

  // Get sorted and filtered lists for display
  const sortedLists = useMemo(() => {
    if (!listsData?.lists) return [];

    let lists = [...listsData.lists];

    // Filter by search query
    if (listsSearchQuery.trim()) {
      const searchLower = listsSearchQuery.toLowerCase();
      lists = lists.filter(
        list =>
          list.name.toLowerCase().includes(searchLower) ||
          (list.description && list.description.toLowerCase().includes(searchLower))
      );
    }

    // Sort
    lists.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (listsSortBy) {
        case 'name':
          aVal = a.name || '';
          bVal = b.name || '';
          return listsSortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        case 'contacts':
          aVal = getContactCountForList(a.campaignId);
          bVal = getContactCountForList(b.campaignId);
          return listsSortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        case 'createdAt':
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
          return listsSortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        default:
          return 0;
      }
    });

    return lists;
  }, [listsData?.lists, listsSortBy, listsSortOrder, listsSearchQuery]);

  if (!companyId || !dataSourceId) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Send className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Select an Email Marketing Platform
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Choose a company and email platform (GetResponse, EmailOctopus, etc.)
          </p>
          <p className="text-xs text-gray-500">
            Go to Admin → Companies → Add email marketing integration
          </p>
        </div>
      </div>
    );
  }

  if (!getResponseDataSource) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <XCircle className="w-16 h-16 mx-auto text-red-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No Email Marketing Platform Configured
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            No GetResponse, EmailOctopus, or other email platform found
          </p>
          <p className="text-xs text-gray-500">
            Add an email marketing integration in Company Settings
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'contacts' as const, name: 'Contacts', icon: Users, count: contactsData?.total || 0 },
    { id: 'lists' as const, name: 'Lists', icon: List, count: listsData?.total || 0 },
    {
      id: 'campaigns' as const,
      name: 'Campaigns',
      icon: Mail,
      count: campaignsData?.campaigns?.length || 0,
    },
    { id: 'sync' as const, name: 'Sync & Automation', icon: Zap, count: null },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Send className="w-8 h-8 text-indigo-600" />
            Email Marketing
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Unified platform for {platformName} and all email marketing providers
          </p>
          {emailMarketingPlatforms.length > 1 && (
            <p className="text-sm text-indigo-600 dark:text-indigo-400 mt-1">
              {emailMarketingPlatforms.length} platforms connected
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-3">
            {/* Live WebSocket Indicator */}
            <div className="flex items-center gap-2 px-3 py-1 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-full">
              <div
                className={`w-2 h-2 rounded-full ${
                  syncing
                    ? 'bg-green-600 animate-pulse'
                    : isWebSocketConnected
                    ? 'bg-green-500 animate-pulse'
                    : 'bg-yellow-500'
                }`}
              ></div>
              <span className="text-xs font-medium text-green-700 dark:text-green-300">
                {syncing
                  ? 'Syncing...'
                  : isWebSocketConnected
                  ? '⚡ Live (WebSocket)'
                  : '🔄 Polling'}
              </span>
            </div>

            {/* Manual Sync Button */}
            <button
              onClick={handleRefresh}
              disabled={syncing}
              className="btn btn-secondary flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Sync Now</span>
            </button>
          </div>

          {lastSyncTime && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Last synced: {lastSyncTime.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {/* Platform Selector & Connection Info */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-xs text-blue-600 dark:text-blue-400 font-medium uppercase tracking-wide mb-1">
                Active Platform
              </p>
              <p className="font-bold text-blue-900 dark:text-blue-100 text-lg">{platformName}</p>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                {getResponseDataSource.config.getresponseEndpoint ||
                  getResponseDataSource.config.emailoctopusEndpoint ||
                  getResponseDataSource.name}
              </p>
            </div>
          </div>

          {/* Platform Switcher - Always visible */}
          <div className="flex flex-col items-end gap-2">
            <label className="text-xs text-blue-600 dark:text-blue-400 font-medium uppercase tracking-wide">
              Email Platform{' '}
              {emailMarketingPlatforms.length > 1 && `(${emailMarketingPlatforms.length})`}
            </label>
            <select
              value={selectedPlatform}
              onChange={e => setSelectedPlatform(e.target.value)}
              disabled={emailMarketingPlatforms.length === 1}
              className="min-w-[240px] px-4 py-2.5 border-2 border-blue-300 dark:border-blue-600 rounded-lg bg-white dark:bg-gray-800 text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed hover:border-indigo-500 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-200 transition-all"
              title={
                emailMarketingPlatforms.length === 1
                  ? 'Only one platform configured. Add more in Company Settings.'
                  : 'Switch between email platforms'
              }
            >
              {emailMarketingPlatforms.map(platform => (
                <option key={platform.id} value={platform.id}>
                  📧 {platform.name} ({platform.type?.toUpperCase()})
                </option>
              ))}
            </select>
            {emailMarketingPlatforms.length === 1 && (
              <p className="text-xs text-blue-600 dark:text-blue-400 italic">
                Add more platforms in Company Settings
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
                {tab.count !== null && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs ${
                      activeTab === tab.id
                        ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div className="space-y-4">
              {/* Actions Bar */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by email..."
                      value={searchQuery}
                      onChange={e => {
                        setSearchQuery(e.target.value);
                        setContactsPage(1); // Reset to first page
                      }}
                      className="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                <select
                  value={selectedList}
                  onChange={e => {
                    setSelectedList(e.target.value);
                    setContactsPage(1); // Reset to first page
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white sm:w-64"
                >
                  <option value="">All Lists</option>
                  {listsData?.lists?.map((list: CampaignList) => (
                    <option key={list.campaignId} value={list.campaignId}>
                      {list.name}
                    </option>
                  ))}
                </select>

                {/* View Toggle */}
                <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
                  <button
                    onClick={() => setViewMode('card')}
                    className={`p-2 rounded ${
                      viewMode === 'card'
                        ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 shadow'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                    title="Card View"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded ${
                      viewMode === 'list'
                        ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 shadow'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                    title="List View"
                  >
                    <LayoutList className="w-4 h-4" />
                  </button>
                </div>

                <button
                  onClick={() => setShowCreateContactModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Contact
                </button>

                <button
                  onClick={() => setShowImportModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Import from E-shop
                </button>
              </div>

              {/* Bulk Actions Bar */}
              {selectedContacts.size > 0 && (
                <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {selectedContacts.size} contact(s) selected
                      </span>
                      <button
                        onClick={() => setSelectedContacts(new Set())}
                        className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700"
                      >
                        Clear selection
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <button
                          onClick={() => setShowBulkActions(!showBulkActions)}
                          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                          <Filter className="w-4 h-4" />
                          Bulk Actions
                        </button>
                        {showBulkActions && (
                          <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10">
                            <div className="p-2">
                              <button
                                onClick={() => {
                                  if (confirm(`Delete ${selectedContacts.size} contact(s)?`)) {
                                    bulkDeleteMutation.mutate(Array.from(selectedContacts));
                                  }
                                }}
                                disabled={bulkDeleteMutation.isPending}
                                className="w-full px-3 py-2 text-left hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md text-red-600 dark:text-red-400"
                              >
                                Delete Selected
                              </button>
                              {listsData?.lists && listsData.lists.length > 1 && (
                                <>
                                  <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400 px-3 py-2 font-medium">
                                    MOVE TO LIST
                                  </div>
                                  {listsData.lists.map((list: CampaignList) => (
                                    <button
                                      key={list.campaignId}
                                      onClick={() => {
                                        bulkMoveMutation.mutate({
                                          contactIds: Array.from(selectedContacts),
                                          targetCampaignId: list.campaignId,
                                        });
                                      }}
                                      disabled={bulkMoveMutation.isPending}
                                      className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md text-sm"
                                    >
                                      {list.name}
                                    </button>
                                  ))}
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Contacts Display - Card or List View */}
              {contactsLoading ? (
                <div className="flex items-center justify-center h-64">
                  <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
                </div>
              ) : viewMode === 'card' ? (
                /* Card View */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {contactsData?.contacts?.length === 0 ? (
                    <div className="col-span-full text-center py-12">
                      <Users className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                      <p className="text-gray-600 dark:text-gray-400 font-medium">
                        No contacts found
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                        Add contacts or import from your e-shop
                      </p>
                    </div>
                  ) : (
                    contactsData?.contacts?.map((contact: Contact) => (
                      <div
                        key={contact.contactId}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-indigo-300 dark:hover:border-indigo-600 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 dark:text-white">
                              {contact.email}
                            </h4>
                            {contact.name && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {contact.name}
                              </p>
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={selectedContacts.has(contact.contactId)}
                            onChange={e => {
                              const newSelected = new Set(selectedContacts);
                              if (e.target.checked) {
                                newSelected.add(contact.contactId);
                              } else {
                                newSelected.delete(contact.contactId);
                              }
                              setSelectedContacts(newSelected);
                            }}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </div>
                        <div className="mb-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400">List</div>
                          <div className="text-sm text-gray-900 dark:text-white">
                            {contact.campaign.name}
                          </div>
                        </div>
                        {contact.tags && contact.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {contact.tags.slice(0, 3).map(tag => (
                              <span
                                key={tag.tagId}
                                className="px-2 py-1 text-xs bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded-full"
                              >
                                {tag.name}
                              </span>
                            ))}
                            {contact.tags.length > 3 && (
                              <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                                +{contact.tags.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                          Added: {new Date(contact.createdOn).toLocaleDateString()}
                        </div>
                        <div className="flex gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                          <button
                            onClick={() => {
                              setViewingContact(contact);
                              setDetailModalStartInEditMode(false);
                              setShowDetailContactModal(true);
                            }}
                            className="flex-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 font-medium"
                          >
                            View
                          </button>
                          <button
                            onClick={() => {
                              setViewingContact(contact);
                              setDetailModalStartInEditMode(true);
                              setShowDetailContactModal(true);
                            }}
                            className="flex-1 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              if (confirm(`Delete contact ${contact.email}?`)) {
                                deleteContactMutation.mutate(contact.contactId);
                              }
                            }}
                            disabled={deleteContactMutation.isPending}
                            className="flex-1 text-sm text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 font-medium disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                /* List View - Datatable with Sorting */
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr>
                          <th className="px-4 py-3 text-left">
                            <input
                              type="checkbox"
                              checked={
                                contactsData?.contacts?.length > 0 &&
                                contactsData.contacts.every((c: Contact) =>
                                  selectedContacts.has(c.contactId)
                                )
                              }
                              onChange={e => {
                                if (e.target.checked) {
                                  setSelectedContacts(
                                    new Set(
                                      contactsData?.contacts?.map((c: Contact) => c.contactId)
                                    )
                                  );
                                } else {
                                  setSelectedContacts(new Set());
                                }
                              }}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            onClick={() => handleContactsSort('email')}
                          >
                            <div className="flex items-center gap-2">
                              Email
                              {sortBy === 'email' && (
                                <ArrowUpDown
                                  className={`w-3 h-3 ${sortOrder === 'desc' ? 'rotate-180' : ''}`}
                                />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            onClick={() => handleContactsSort('name')}
                          >
                            <div className="flex items-center gap-2">
                              Name
                              {sortBy === 'name' && (
                                <ArrowUpDown
                                  className={`w-3 h-3 ${sortOrder === 'desc' ? 'rotate-180' : ''}`}
                                />
                              )}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            List
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Tags
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            onClick={() => handleContactsSort('createdOn')}
                          >
                            <div className="flex items-center gap-2">
                              Added
                              {sortBy === 'createdOn' && (
                                <ArrowUpDown
                                  className={`w-3 h-3 ${sortOrder === 'desc' ? 'rotate-180' : ''}`}
                                />
                              )}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {contactsData?.contacts?.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-6 py-12 text-center">
                              <Users className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                              <p className="text-gray-600 dark:text-gray-400 font-medium">
                                No contacts found
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                                Add contacts or import from your e-shop
                              </p>
                            </td>
                          </tr>
                        ) : (
                          contactsData?.contacts?.map((contact: Contact) => (
                            <tr
                              key={contact.contactId}
                              className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                                selectedContacts.has(contact.contactId)
                                  ? 'bg-indigo-50 dark:bg-indigo-900/20'
                                  : ''
                              }`}
                            >
                              <td className="px-4 py-4">
                                <input
                                  type="checkbox"
                                  checked={selectedContacts.has(contact.contactId)}
                                  onChange={e => {
                                    const newSelected = new Set(selectedContacts);
                                    if (e.target.checked) {
                                      newSelected.add(contact.contactId);
                                    } else {
                                      newSelected.delete(contact.contactId);
                                    }
                                    setSelectedContacts(newSelected);
                                  }}
                                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                              </td>
                              <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                                {contact.email}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                                {contact.name || '-'}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                                {contact.campaign.name}
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex flex-wrap gap-1">
                                  {contact.tags?.slice(0, 3).map(tag => (
                                    <span
                                      key={tag.tagId}
                                      className="px-2 py-1 text-xs bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded-full"
                                    >
                                      {tag.name}
                                    </span>
                                  ))}
                                  {contact.tags && contact.tags.length > 3 && (
                                    <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                                      +{contact.tags.length - 3}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                                {new Date(contact.createdOn).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 text-right text-sm space-x-3">
                                <button
                                  onClick={() => {
                                    setViewingContact(contact);
                                    setDetailModalStartInEditMode(false);
                                    setShowDetailContactModal(true);
                                  }}
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 font-medium"
                                >
                                  View
                                </button>
                                <span className="text-gray-300 dark:text-gray-600">•</span>
                                <button
                                  onClick={() => {
                                    setViewingContact(contact);
                                    setDetailModalStartInEditMode(true);
                                    setShowDetailContactModal(true);
                                  }}
                                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 font-medium"
                                >
                                  Edit
                                </button>
                                <span className="text-gray-300 dark:text-gray-600">•</span>
                                <button
                                  onClick={() => {
                                    if (confirm(`Delete contact ${contact.email}?`)) {
                                      deleteContactMutation.mutate(contact.contactId);
                                    }
                                  }}
                                  disabled={deleteContactMutation.isPending}
                                  className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 font-medium disabled:opacity-50"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Pagination Controls */}
              {contactsData && contactsData.total > 0 && (
                <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => goToContactsPage(contactsPage - 1)}
                      disabled={!contactsData.hasPrev}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => goToContactsPage(contactsPage + 1)}
                      disabled={!contactsData.hasNext}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Showing{' '}
                        <span className="font-medium">
                          {(contactsPage - 1) * contactsLimit + 1}
                        </span>{' '}
                        to{' '}
                        <span className="font-medium">
                          {Math.min(contactsPage * contactsLimit, contactsData.total)}
                        </span>{' '}
                        of <span className="font-medium">{contactsData.total}</span> contacts
                      </p>
                    </div>
                    <div>
                      <nav
                        className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                        aria-label="Pagination"
                      >
                        <button
                          onClick={() => goToContactsPage(contactsPage - 1)}
                          disabled={!contactsData.hasPrev}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        {[...Array(Math.min(5, contactsData.totalPages))].map((_, idx) => {
                          const pageNumber = Math.max(1, contactsPage - 2) + idx;
                          if (pageNumber > contactsData.totalPages) return null;
                          return (
                            <button
                              key={pageNumber}
                              onClick={() => goToContactsPage(pageNumber)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pageNumber === contactsPage
                                  ? 'z-10 bg-indigo-50 dark:bg-indigo-900/20 border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        })}
                        <button
                          onClick={() => goToContactsPage(contactsPage + 1)}
                          disabled={!contactsData.hasNext}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Lists Tab */}
          {activeTab === 'lists' && (
            <div className="space-y-4">
              {/* Actions Bar - Consistent with Contacts Tab */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search lists..."
                      value={listsSearchQuery}
                      onChange={e => setListsSearchQuery(e.target.value)}
                      className="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                    {listsSearchQuery && (
                      <button
                        onClick={() => setListsSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* View Toggle for Lists */}
                <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
                  <button
                    onClick={() => setListsViewMode('card')}
                    className={`p-2 rounded ${
                      listsViewMode === 'card'
                        ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 shadow'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                    title="Card View"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setListsViewMode('list')}
                    className={`p-2 rounded ${
                      listsViewMode === 'list'
                        ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 shadow'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                    title="Table View"
                  >
                    <LayoutList className="w-4 h-4" />
                  </button>
                </div>

                <button
                  onClick={() => setShowCreateListModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
                >
                  <Plus className="w-4 h-4" />
                  Create List
                </button>
              </div>

              {listsLoading ? (
                <div className="flex items-center justify-center h-64">
                  <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
                </div>
              ) : listsViewMode === 'card' ? (
                /* Card View for Lists */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sortedLists?.length === 0 ? (
                    <div className="col-span-full text-center py-12">
                      <List className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                      <p className="text-gray-600 dark:text-gray-400">No lists found</p>
                      <p className="text-sm text-gray-500 mt-1">Create your first contact list</p>
                    </div>
                  ) : (
                    sortedLists?.map((list: CampaignList) => {
                      const contactCount = getContactCountForList(list.campaignId);

                      return (
                        <div
                          key={list.campaignId}
                          onClick={() => {
                            setActiveTab('contacts');
                            setSelectedList(list.campaignId);
                            toast.success(`Showing contacts from "${list.name}"`);
                          }}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-indigo-400 hover:shadow-lg transition-all cursor-pointer group"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-semibold text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                              {list.name}
                            </h4>
                            {list.isDefault && (
                              <span className="px-2 py-1 text-xs bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded-full">
                                Default
                              </span>
                            )}
                          </div>
                          {list.description && list.description !== list.name && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                              {list.description}
                            </p>
                          )}

                          <div className="mb-3 p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  Contacts
                                </span>
                              </div>
                              <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                                {contactCount}
                              </span>
                            </div>
                            {contactCount > 0 && (
                              <div className="mt-2 pt-2 border-t border-indigo-200 dark:border-indigo-700">
                                <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                                  <span>⚠️</span>
                                  Deleting this list won't delete contacts
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-3">
                            <span>{list.languageCode}</span>
                            <span>{new Date(list.createdAt).toLocaleDateString()}</span>
                          </div>

                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex gap-2">
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                setViewingList(list);
                                setShowViewListModal(true);
                              }}
                              className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 font-medium"
                            >
                              View
                            </button>
                            <span className="text-gray-300 dark:text-gray-600">•</span>
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                setEditingList(list);
                                setEditListName(list.name);
                                setEditListDescription(list.description || '');
                                setEditListIsDefault(list.isDefault || false);
                                setShowEditListModal(true);
                              }}
                              className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
                            >
                              Edit
                            </button>
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                const contactsInList = getContactCountForList(list.campaignId);
                                if (contactsInList > 0) {
                                  const confirmMsg =
                                    `⚠️ WARNING: This list contains ${contactsInList} contact(s)!\n\n` +
                                    `Deleting "${list.name}" will NOT delete the contacts.\n` +
                                    `The contacts will remain in GetResponse but won't be in any list.\n\n` +
                                    `Are you sure you want to delete this list?`;
                                  if (confirm(confirmMsg)) {
                                    deleteListMutation.mutate(list.campaignId);
                                  }
                                } else {
                                  if (
                                    confirm(`Delete list "${list.name}"? This cannot be undone.`)
                                  ) {
                                    deleteListMutation.mutate(list.campaignId);
                                  }
                                }
                              }}
                              className="text-sm text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                /* Table View for Lists */
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            onClick={() => handleListsSort('name')}
                          >
                            <div className="flex items-center gap-2">
                              List Name
                              {listsSortBy === 'name' && (
                                <ArrowUpDown
                                  className={`w-3 h-3 ${
                                    listsSortOrder === 'desc' ? 'rotate-180' : ''
                                  }`}
                                />
                              )}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Description
                          </th>
                          <th
                            className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            onClick={() => handleListsSort('contacts')}
                          >
                            <div className="flex items-center justify-center gap-2">
                              Contacts
                              {listsSortBy === 'contacts' && (
                                <ArrowUpDown
                                  className={`w-3 h-3 ${
                                    listsSortOrder === 'desc' ? 'rotate-180' : ''
                                  }`}
                                />
                              )}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Language
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            onClick={() => handleListsSort('createdAt')}
                          >
                            <div className="flex items-center gap-2">
                              Created
                              {listsSortBy === 'createdAt' && (
                                <ArrowUpDown
                                  className={`w-3 h-3 ${
                                    listsSortOrder === 'desc' ? 'rotate-180' : ''
                                  }`}
                                />
                              )}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {sortedLists?.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-6 py-12 text-center">
                              <List className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                              <p className="text-gray-600 dark:text-gray-400 font-medium">
                                No lists found
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                                Create your first contact list
                              </p>
                            </td>
                          </tr>
                        ) : (
                          sortedLists?.map((list: CampaignList) => {
                            const contactCount = getContactCountForList(list.campaignId);

                            return (
                              <tr
                                key={list.campaignId}
                                onClick={() => {
                                  setActiveTab('contacts');
                                  setSelectedList(list.campaignId);
                                  toast.success(`Showing contacts from "${list.name}"`);
                                }}
                                className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                              >
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                      {list.name}
                                    </span>
                                    {list.isDefault && (
                                      <span className="px-2 py-0.5 text-xs bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded-full">
                                        Default
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                                  {list.description && list.description !== list.name
                                    ? list.description
                                    : '-'}
                                </td>
                                <td className="px-6 py-4 text-center">
                                  <div className="flex items-center justify-center gap-2">
                                    <Users className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                    <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                                      {contactCount}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-center text-sm text-gray-600 dark:text-gray-400">
                                  {list.languageCode}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                                  {new Date(list.createdAt).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 text-right text-sm space-x-3">
                                  <button
                                    onClick={e => {
                                      e.stopPropagation();
                                      setViewingList(list);
                                      setShowViewListModal(true);
                                    }}
                                    className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 font-medium"
                                  >
                                    View
                                  </button>
                                  <button
                                    onClick={e => {
                                      e.stopPropagation();
                                      setEditingList(list);
                                      setEditListName(list.name);
                                      setEditListDescription(list.description || '');
                                      setEditListIsDefault(list.isDefault || false);
                                      setShowEditListModal(true);
                                    }}
                                    className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 font-medium"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={e => {
                                      e.stopPropagation();
                                      const contactsInList = getContactCountForList(
                                        list.campaignId
                                      );
                                      if (contactsInList > 0) {
                                        const confirmMsg =
                                          `⚠️ WARNING: This list contains ${contactsInList} contact(s)!\n\n` +
                                          `Deleting "${list.name}" will NOT delete the contacts.\n` +
                                          `The contacts will remain in GetResponse but won't be in any list.\n\n` +
                                          `Are you sure you want to delete this list?`;
                                        if (confirm(confirmMsg)) {
                                          deleteListMutation.mutate(list.campaignId);
                                        }
                                      } else {
                                        if (
                                          confirm(
                                            `Delete list "${list.name}"? This cannot be undone.`
                                          )
                                        ) {
                                          deleteListMutation.mutate(list.campaignId);
                                        }
                                      }
                                    }}
                                    className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 font-medium"
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Campaigns Tab */}
          {activeTab === 'campaigns' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Email Campaigns</h3>
                <button className="btn btn-primary">
                  <Plus className="w-4 h-4" />
                  Create Campaign
                </button>
              </div>

              {campaignsLoading ? (
                <div className="flex items-center justify-center h-64">
                  <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Campaign
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Recipients
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Open Rate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Click Rate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          CTOR
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Engagement
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Send Date
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {campaignsData?.campaigns?.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-6 py-12 text-center">
                            <Mail className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                            <p className="text-gray-600 dark:text-gray-400">No campaigns found</p>
                            <p className="text-sm text-gray-500 mt-1">
                              Create your first email campaign
                            </p>
                          </td>
                        </tr>
                      ) : (
                        campaignsData?.campaigns?.map((campaign: any) => (
                          <tr
                            key={campaign.campaign_id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                          >
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {campaign.campaign_name}
                              </div>
                              <div className="text-xs text-gray-500">{campaign.subject_line}</div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                              {campaign.total_recipients?.toLocaleString()}
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {campaign.open_rate?.toFixed(1)}%
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {campaign.click_rate?.toFixed(1)}%
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {campaign.click_to_open_rate?.toFixed(1)}%
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                  <div
                                    className="bg-indigo-600 h-2 rounded-full"
                                    style={{
                                      width: `${Math.min(100, campaign.engagement_score)}%`,
                                    }}
                                  />
                                </div>
                                <span className="text-sm font-medium">
                                  {campaign.engagement_score?.toFixed(0)}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                              {new Date(campaign.send_date).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 text-right text-sm">
                              <button className="text-indigo-600 hover:text-indigo-900">
                                View
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Sync & Automation Tab */}
          {activeTab === 'sync' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">E-shop Integration & Automation</h3>

              {/* Abandoned Cart Sync */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center">
                      <ShoppingCart className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        Abandoned Cart Recovery
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Automatically sync abandoned carts to GetResponse
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Target List</label>
                    <select className="input">
                      <option value="">Select list...</option>
                      {listsData?.lists?.map((list: CampaignList) => (
                        <option key={list.campaignId} value={list.campaignId}>
                          {list.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Delay (minutes)</label>
                    <input type="number" defaultValue="60" className="input" />
                  </div>
                </div>

                <button
                  onClick={() => syncAbandonedCartsMutation.mutate()}
                  disabled={syncAbandonedCartsMutation.isPending}
                  className="btn btn-secondary w-full sm:w-auto"
                >
                  {syncAbandonedCartsMutation.isPending ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      Sync Now
                    </>
                  )}
                </button>

                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">0</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Carts Today</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">0</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Synced</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">0%</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Recovery Rate</div>
                  </div>
                </div>
              </div>

              {/* Abandoned Consultation */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        Abandoned Consultation Follow-up
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Follow up with users who didn't complete consultations
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Target List</label>
                    <select className="input">
                      <option value="">Select list...</option>
                      {listsData?.lists?.map((list: CampaignList) => (
                        <option key={list.campaignId} value={list.campaignId}>
                          {list.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Delay (minutes)</label>
                    <input type="number" defaultValue="30" className="input" />
                  </div>
                </div>

                <button className="btn btn-secondary w-full sm:w-auto">
                  <Zap className="w-4 h-4" />
                  Sync Now
                </button>

                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">0</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Today</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">0</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Synced</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">0%</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Completion Rate</div>
                  </div>
                </div>
              </div>

              {/* Sync History */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
                  Recent Sync Activity
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        No sync activity yet
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      {activeTab !== 'sync' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Contacts</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {contactsData?.total || 0}
                </p>
              </div>
              <Users className="w-8 h-8 text-indigo-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Lists</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {listsData?.total || 0}
                </p>
              </div>
              <List className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Campaigns</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {campaignsData?.campaigns?.length || 0}
                </p>
              </div>
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Avg Open Rate</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {campaignsData?.overall?.avg_open_rate?.toFixed(1) || 0}%
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>
      )}

      {/* Create List Modal */}
      {showCreateListModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <List className="w-6 h-6 text-indigo-600" />
                Create New List
              </h3>
              <button
                onClick={() => {
                  setShowCreateListModal(false);
                  setNewListName('');
                  setNewListDescription('');
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  List Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newListName}
                  onChange={e => setNewListName(e.target.value)}
                  placeholder="e.g., Newsletter Subscribers"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={newListDescription}
                  onChange={e => setNewListDescription(e.target.value)}
                  placeholder="e.g., Customers who signed up for our newsletter"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  <CheckCircle className="w-3 h-3 inline mr-1" />
                  This list will be created in GetResponse and synced to MongoDB for analytics
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowCreateListModal(false);
                  setNewListName('');
                  setNewListDescription('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => createListMutation.mutate()}
                disabled={!newListName || createListMutation.isPending}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {createListMutation.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create List
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit List Modal */}
      {showEditListModal && editingList && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Settings className="w-6 h-6 text-indigo-600" />
                Edit List
              </h3>
              <button
                onClick={() => {
                  setShowEditListModal(false);
                  setEditingList(null);
                  setEditListName('');
                  setEditListDescription('');
                  setEditListIsDefault(false);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  List Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editListName}
                  onChange={e => {
                    console.log('📝 Name changed to:', e.target.value);
                    setEditListName(e.target.value);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={editListDescription}
                  onChange={e => {
                    console.log('📝 Description changed to:', e.target.value);
                    setEditListDescription(e.target.value);
                  }}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Optional description for this list"
                />
              </div>

              {/* Set as Default Toggle */}
              <div className="flex items-center justify-between p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-900 dark:text-white">
                    Set as Default List
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    New contacts will be added to this list by default
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditListIsDefault(!editListIsDefault)}
                  className={`
                    relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                    ${editListIsDefault ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'}
                  `}
                >
                  <span
                    className={`
                      inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                      ${editListIsDefault ? 'translate-x-6' : 'translate-x-1'}
                    `}
                  />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowEditListModal(false);
                  setEditingList(null);
                  setEditListName('');
                  setEditListDescription('');
                  setEditListIsDefault(false);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (editingList) {
                    console.log('🔧 [Edit List] Saving:', {
                      listId: editingList.campaignId,
                      name: editListName,
                      description: editListDescription,
                      isDefault: editListIsDefault,
                      'Are they different?': editListName !== editListDescription,
                    });
                    updateListMutation.mutate({
                      listId: editingList.campaignId,
                      name: editListName,
                      description: editListDescription,
                      isDefault: editListIsDefault,
                    });
                  }
                }}
                disabled={!editListName || updateListMutation.isPending}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {updateListMutation.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View List Modal - Shows list details, overview, and insights */}
      <ListDetailModal
        isOpen={showViewListModal}
        onClose={() => {
          setShowViewListModal(false);
          setViewingList(null);
        }}
        list={viewingList}
        dataSourceId={emailMarketingDataSource?.id}
      />

      {/* Full List Settings Modal (Category, Language, Logo, etc.) */}
      {showListSettings && settingsList && (
        <ListSettingsModal
          isOpen={showListSettings}
          onClose={() => {
            setShowListSettings(false);
            setSettingsList(null);
          }}
          list={settingsList}
          onSave={async (listId, settings) => {
            // Save full settings via API
            const response = await fetch(`${''}/api/v1/getresponse/lists/${listId}/settings`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({
                companyId,
                dataSourceId,
                ...settings,
              }),
            });

            if (!response.ok) {
              throw new Error('Failed to save settings');
            }

            toast.success('✅ List settings saved successfully!');
            refetchLists();
          }}
        />
      )}

      {/* Contact Create Modal */}
      <ContactCreateModal
        isOpen={showCreateContactModal}
        onClose={() => setShowCreateContactModal(false)}
        companyId={companyId || ''}
        dataSourceId={dataSourceId || ''}
        lists={listsData?.lists || []}
        defaultListId={selectedList}
      />

      {/* Contact Detail Modal */}
      <ContactDetailModal
        isOpen={showDetailContactModal}
        onClose={() => {
          setShowDetailContactModal(false);
          setViewingContact(null);
          setDetailModalStartInEditMode(false);
        }}
        contact={viewingContact}
        companyId={companyId || ''}
        dataSourceId={dataSourceId || ''}
        startInEditMode={detailModalStartInEditMode}
        onEditClick={() => {
          setShowDetailContactModal(false);
          setEditingContact(viewingContact);
          setShowEditContactModal(true);
        }}
      />

      {/* Contact Edit Modal */}
      <ContactEditModal
        isOpen={showEditContactModal}
        onClose={() => {
          setShowEditContactModal(false);
          setEditingContact(null);
        }}
        contact={editingContact}
        companyId={companyId || ''}
        dataSourceId={dataSourceId || ''}
        lists={listsData?.lists || []}
      />

      {/* Import from E-shop Modal */}
      <ImportFromEshopModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        companyId={companyId || ''}
        dataSourceId={dataSourceId || ''}
        targetListId={selectedList}
        lists={(listsData?.lists || []).map((list: CampaignList) => ({
          ...list,
          contactCount: getContactCountForList(list.campaignId),
        }))}
      />
    </div>
  );
};

export default GetResponsePage;
